import React, { useState, useEffect, useRef, useMemo } from "react";
import { Container, Button, Progress } from "reactstrap";
import {
  useNavigate,
  useLocation,
  Routes,
  Route,
  useParams,
} from "react-router-dom";
import styles from "modules/Library/AdverseEvent/css/adverseEvent.module.scss";
import tabStyles from "modules/PrintLabel/MainPrintListTabs/css/labeltypetabs.module.scss";
import ac from "../css/accessioning.module.scss";
import SubjectToVacutainer from "../components/SubjectToVacutainer";
import VacutainerToAliquots from "../components/VacutainerToAliquots";
import { useSessionService } from "modules/Session/Session/api/useSession";
import {
  useScanningScanSampleCollectionService,
  useScanningScanSampleSeperationService,
  useSampleCollectionScanProgressService,
  useSampleSeperationScanProgressService,
} from "modules/ScanningApp/api/useScanningSession";
import {
  CurrentUser,
  GetUserData,
  showToastMessage,
} from "common/service/CommonService";
import { ACCESSIONING_PATHS } from "Routes/paths";
import { queryClient } from "api/queryClient";
import FullPageLoader from "Components/Common/FullPageLoader";
import SessionProgressModal from "../components/SessionProgressModal";

type AccessioningTabKey = "step1" | "step2";

const ActiveSessionResponsive: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  // Active Tab & Progress Modal States
  const [activeTab, setActiveTab] = useState<AccessioningTabKey>("step1");
  const [showProgressModal, setShowProgressModal] = useState(false);
  const isManualAction = useRef(false);

  const isStep2Url = useMemo(() => {
    return location.pathname.endsWith("/step2") || location.pathname.includes("/step2");
  }, [location.pathname]);

  // Sync tab from URL path
  useEffect(() => {
    const segments = location.pathname.split("/").filter(Boolean);
    const last = segments[segments.length - 1];
    setActiveTab(last === "step2" ? "step2" : "step1");
  }, [location.pathname]);

  const toggleTab = (tabKey: AccessioningTabKey): void => {
    const basePath = ACCESSIONING_PATHS.SESSION.replace(":id", id || "");
    navigate(tabKey === "step1" ? basePath : `${basePath}/${tabKey}`);
    setActiveTab(tabKey);
  };

  // Session Data & Status Hooks
  const user = GetUserData() as CurrentUser;

  // Step 1 API: ScanSampleCollection/Session/Select
  const { SampleCollectionSessionSelectQuery } = useScanningScanSampleCollectionService({
    sessionid: id ? Number(id) : null,
    enabled: !isStep2Url && !!id,
  });

  // Step 2 API: ScanSampleSeperation/Session/Select
  const { SampleSeperationSessionSelectQuery } = useScanningScanSampleSeperationService({
    sessionid: id ? Number(id) : null,
    enabled: isStep2Url && !!id,
  });

  // Fallback Session API
  const { UpdateMutation } = useSessionService();

  const sessionIdNum = id ? Number(id) : null;
  const { SampleCollectionScanProgressQuery } = useSampleCollectionScanProgressService({
    sessionid: sessionIdNum,
    currentpage: null,
    pagesize: null,
    tabstatus: "ALL",
    enabled: !isStep2Url && !!sessionIdNum,
  });

  const { SampleSeperationScanProgressQuery } = useSampleSeperationScanProgressService({
    sessionid: sessionIdNum,
    currentpage: null,
    pagesize: null,
    tabstatus: "ALL",
    enabled: isStep2Url && !!sessionIdNum,
  });

  const sessionData = useMemo(() => {
    if (isStep2Url) {
      return SampleSeperationSessionSelectQuery?.data?.data?.[0];
    }
    return SampleCollectionSessionSelectQuery?.data?.data?.[0];
  }, [
    isStep2Url,
    SampleSeperationSessionSelectQuery?.data?.data,
    SampleCollectionSessionSelectQuery?.data?.data,
  ]);

  const isSampleSeparation = useMemo(() => {
    const act = sessionData?.sessionactivity;
    return isStep2Url || act === "sample_separation" || act === "vacutainer_to_aliquot";
  }, [isStep2Url, sessionData?.sessionactivity]);

  const totalSubs = sessionData?.totalsubjects || 0;
  const totalAliquotsPerSub = sessionData?.aliquotspersubject || 0;

  const completedSubs = useMemo(() => {
    if (isSampleSeparation) {
      if (totalAliquotsPerSub > 0) {
        return Math.floor((sessionData?.completedaliquots || 0) / totalAliquotsPerSub) + (sessionData?.skippedsubjects || 0);
      }
      return 0;
    }
    return (sessionData?.completedsubjects || 0) + (sessionData?.skippedsubjects || 0);
  }, [isSampleSeparation, sessionData, totalAliquotsPerSub]);

  const handleUpdateStatus = (
    status: "paused" | "completed" | "active",
    shouldNavigate = true,
  ) => {
    if (shouldNavigate) isManualAction.current = true;
    const payload = {
      sessionid: id ? parseInt(id) : 1,
      sessionstate: status,
      updatereason: "Update Session Status",
    };

    UpdateMutation.mutate(payload, {
      onError: (err: any) => {
        showToastMessage("error", err || `Failed to ${status} session`);
      },
      onSuccess: (res: any) => {
        queryClient.invalidateQueries({ queryKey: ["scan-sample-collection-session-select-all"] });
        queryClient.invalidateQueries({ queryKey: ["scan-sample-seperation-session-select-all"] });
        queryClient.invalidateQueries({ queryKey: ["sample-collection-session-select"] });
        queryClient.invalidateQueries({ queryKey: ["sample-seperation-session-select"] });
        if (shouldNavigate) {
          showToastMessage(
            "success",
            res?.message || `Session ${status} successfully`,
          );
          navigate(ACCESSIONING_PATHS.LIST);
        } else {
          showToastMessage(
            "success",
            res?.message || `Session ${status} successfully`,
          );
        }
      },
    });
  };

  const displayStatusRef = useRef(sessionData?.display_status);
  useEffect(() => {
    displayStatusRef.current = sessionData?.display_status;
  }, [sessionData?.display_status]);

  // Auto-pause session when navigating away
  useEffect(() => {
    return () => {
      if (!isManualAction.current && displayStatusRef.current !== "PAUSED") {
        handleUpdateStatus("paused", false);
      }
    };
  }, []);

  const isQueryLoading = isSampleSeparation
    ? SampleSeperationSessionSelectQuery.isPending
    : (SampleCollectionSessionSelectQuery.isPending);

  if (isQueryLoading && !sessionData) {
    return <FullPageLoader />;
  }

  return (
    <React.Fragment>
      <div className={`page-content ${ac.pageContent}`}>
        {UpdateMutation.isPending && <FullPageLoader />}
        <Container fluid className="pt-0">
          {/* ── Header ── */}
          <div className={ac.pageHeader}>
            <div className="d-flex align-items-center gap-3">
              <Button
                color="light"
                outline
                className="px-2 py-1 bg-white border"
                onClick={() => handleUpdateStatus("paused", true)}
                disabled={UpdateMutation.isPending}
              >
                <i className="ri-arrow-left-line text-dark"></i>
              </Button>
              <div>
                <h5 className={ac.sessionTitle}>
                  Active Session
                  <span
                    className={`badge bg-light text-secondary border fw-normal ms-2 ${ac.sessionBadge}`}
                  >
                    {sessionData?.sessionno ||
                      sessionData?.sessionid ||
                      "SESS-0091"}
                  </span>
                </h5>
                <p className={ac.sessionSubtitle}>
                  Execute and monitor the accessioning workflow for the current
                  study period.
                </p>
              </div>
            </div>
            <div className="d-flex gap-3 align-items-center">
              {sessionData?.display_status === "PAUSED" ? (
                <Button
                  color="success"
                  outline
                  onClick={() => handleUpdateStatus("active", false)}
                  disabled={UpdateMutation.isPending}
                >
                  Resume Session
                </Button>
              ) : (
                <Button
                  color="secondary"
                  outline
                  onClick={() => handleUpdateStatus("paused", true)}
                  disabled={UpdateMutation.isPending}
                >
                  Pause Session
                </Button>
              )}
              <Button
                color="primary"
                outline
                onClick={() => handleUpdateStatus("completed", true)}
                disabled={
                  UpdateMutation.isPending ||
                  completedSubs < totalSubs || totalSubs === 0
                }
              >
                Complete Session
              </Button>
            </div>
          </div>

          {/* ── White Panel ── */}
          <div className={`${styles.listPanel} mb-3`}>
            {/* Study Metadata */}
            <div className="px-4 pt-3 pb-2 border-bottom">
              <div className="d-flex justify-content-between align-items-end">
                <div
                  className={`d-flex align-items-center gap-4 ${ac.metaRow}`}
                >
                  <div>
                    <i className="ri-folder-line me-1 text-primary"></i> Study:
                    <span className="fw-bold text-dark ms-1">
                      {sessionData?.studycode}
                    </span>
                  </div>
                  <div>
                    <i className="ri-calendar-line me-1 text-primary"></i>{" "}
                    Period:
                    <span className="fw-bold text-dark ms-1">
                      {sessionData?.period_number}
                    </span>
                  </div>
                  <div>
                    <i className="ri-time-line me-1 text-primary"></i> Time
                    Point:{" "}
                    <span className="fw-bold text-dark ms-1">
                      {sessionData?.timepoint_number}
                    </span>
                  </div>
                  {!isSampleSeparation && (
                    <div>
                      <i className="ri-building-2-line me-1 text-primary"></i> Dosing Station:{" "}
                      <span className="fw-bold text-dark ms-1">
                        {sessionData?.dosingstationno ? `DS-${String(sessionData.dosingstationno).padStart(2, "0")}` : "DS-01"}
                      </span>
                    </div>
                  )}
                </div>
                <div className={ac.progressWrapper} style={{ width: "260px" }}>
                  <div
                    className={`d-flex justify-content-between text-muted mb-1 ${ac.progressLabel}`}
                  >
                    <span>Session Progress</span>
                    <div className="d-flex align-items-center gap-2">
                      <span className="fw-bold text-success" title="Completed">
                        {completedSubs}
                      </span>
                      <span className="text-muted">/</span>
                      <span className="fw-bold text-dark" title="Total Subjects">
                        {totalSubs}
                      </span>
                      <i
                        className="ri-eye-line text-primary fs-14 ms-1"
                        style={{ cursor: "pointer" }}
                        onClick={() => setShowProgressModal(true)}
                        title="View Detailed Progress"
                      />
                    </div>
                  </div>
                  <Progress multi className={ac.progressBar}>
                    <Progress bar value={(completedSubs / (totalSubs || 1)) * 100} color="success" />
                  </Progress>
                  <div className="d-flex justify-content-between mt-1 text-muted" style={{ fontSize: "10px" }}>
                    <span className="d-flex align-items-center gap-1">
                      <span className="rounded-circle bg-success" style={{ width: "6px", height: "6px", display: "inline-block" }} />
                      Completed
                    </span>
                    <span>
                      Total: {totalSubs}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* ── Tabs ── */}
            <div className={tabStyles.tabContainer}>
              {!isSampleSeparation ? (
                <button
                  type="button"
                  className={`${tabStyles.tabBtn} ${tabStyles.active}`}
                >
                  Subject to Vacutainer
                </button>
              ) : (
                <button
                  type="button"
                  className={`${tabStyles.tabBtn} ${tabStyles.active}`}
                >
                  Vacutainer to Aliquots
                </button>
              )}
            </div>

            {/* ── Content ── */}
            <div>
              {isSampleSeparation ? (
                <VacutainerToAliquots
                  sessionData={sessionData}
                  sessionid={id}
                  isParentModalOpen={showProgressModal}
                />
              ) : (
                <SubjectToVacutainer
                  sessionData={sessionData}
                  sessionid={id}
                  isParentModalOpen={showProgressModal}
                />
              )}
            </div>
          </div>
        </Container>
      </div>

      <SessionProgressModal
        isOpen={showProgressModal}
        toggle={() => setShowProgressModal(!showProgressModal)}
        sessionData={sessionData}
        isSampleSeparation={isSampleSeparation}
      />
    </React.Fragment>
  );
};

export default ActiveSessionResponsive;
